import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, ArrowRight, ShieldCheck, Clock, MapPin, Users, PackageCheck, Sparkles, CheckCircle2, Truck, Star, Info } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageMeta } from "@/components/PageMeta";
import { CtaBand } from "@/components/CtaBand";
import { Section, SectionHeader, Placeholder } from "@/components/layout/Section";
import { AREAS, IMAGES, SERVICES, TRUST_POINTS, CONTACT, BRAND, waLink } from "@/config/brand";
import { HOME } from "@/constants/testIds";
import { HeroQuoteForm } from "@/components/quote/HeroQuoteForm";

const homeFaqs = [
    { q: "How much do removals cost?", a: "Costs depend on volume, distance, floor level, access and any packing help required. We tailor a quote for each move rather than showing an inaccurate automatic price." },
    { q: "How is the quotation calculated?", a: "We consider your inventory, property details at both ends, dates, parking and any special items. Once we understand the job, we’ll send a clear, tailored quotation." },
    { q: "Do you provide packing help?", a: "Yes. We can supply materials and provide hands-on packing help — partial or full — as part of your booking." },
    { q: "Can you move one item?", a: "Yes, we offer single-item delivery for furniture, appliances and other bulky items across London." },
    { q: "Do you cover all of London?", a: "We work across Greater London including Central, North, South, East and West London and most surrounding boroughs." },
    { q: "Can I book at short notice?", a: "Same-day and short-notice moves are subject to availability. Please contact us on WhatsApp for the quickest response." },
    { q: "What information do you need for a quote?", a: "Pickup and drop-off postcodes, property types and floor levels, an approximate inventory, preferred date, and any special items or access notes." },
    { q: "Do we need to arrange parking?", a: "Where possible, please reserve a suitable space or advise us about restrictions so we can plan accordingly. Some boroughs require parking permits." },
    { q: "What happens if the move takes longer than expected?", a: "We’ll explain in advance how the quote is structured so there are no surprises, and we’ll communicate clearly on the day if anything changes." },
    { q: "Can you dismantle and reassemble furniture?", a: "Yes — flag any dismantling requirements when you request your quote and we’ll come prepared with the right tools." },
];

const benefits = [
    { icon: PackageCheck, title: "Careful handling", body: "Protective materials and considerate handling for your belongings." },
    { icon: MessageCircle, title: "Clear communication", body: "WhatsApp updates from booking through to completion." },
    { icon: Clock, title: "Flexible appointments", body: "Evenings and weekends available where possible." },
    { icon: Truck, title: "Suitable vehicle & kit", body: "Right-sized van, trolleys and blankets for the job." },
    { icon: Users, title: "Professional team", body: "Presentable, polite crew who know how to work efficiently." },
    { icon: MapPin, title: "Local London knowledge", body: "Familiar with London traffic, access and permits." },
    { icon: Sparkles, title: "Tailored quotations", body: "Every quote is based on your actual move — no guessing." },
    { icon: ShieldCheck, title: "Considerate service", body: "We treat your home and belongings with respect." },
];

const steps = [
    { title: "Tell us about your move", body: "Share your inventory, dates and pickup/drop-off details." },
    { title: "Receive your quotation", body: "We’ll reply with a clear, tailored quote for the job." },
    { title: "Confirm your booking", body: "Choose your slot and we’ll lock everything in." },
    { title: "We complete the move", body: "Our team arrives on time and takes care of the rest." },
];

function ServiceCard({ service }) {
    const Icon = LucideIcons[service.icon] || Truck;
    return (
        <Link
            to={`/services/${service.slug}`}
            data-testid={HOME.servicesCard}
            className="group rounded-xl bg-white border border-[#17252D]/10 p-5 shadow-[0_1px_2px_rgba(23,37,45,0.06)] hover:shadow-[0_10px_30px_rgba(23,37,45,0.10)] hover:border-[#001B3D]/40 transition-[box-shadow,border-color] duration-200 flex flex-col"
        >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#001B3D]/10 text-[#001B3D]">
                <Icon className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold">{service.title}</h3>
            <p className="mt-1 text-sm text-[#17252D]/75 leading-relaxed">{service.short}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#001B3D] group-hover:gap-2 transition-[gap] duration-200">
                Get a quote <ArrowRight className="h-4 w-4" />
            </span>
        </Link>
    );
}

export default function Home() {
    const ldJson = {
        "@context": "https://schema.org",
        "@type": "MovingCompany",
        name: BRAND.name,
        url: `https://${BRAND.domain}/`,
        telephone: CONTACT.phoneDisplay,
        email: CONTACT.email,
        areaServed: { "@type": "City", name: "London" },
        address: { "@type": "PostalAddress", addressLocality: "London", addressCountry: "GB" },
    };

    return (
        <div>
            <PageMeta
                title="YS Removals — London House & Office Removals"
                description="Reliable house, flat and office removals across London. Careful handling, clear quotations, WhatsApp updates."
                canonical={`https://${BRAND.domain}/`}
                image={IMAGES.heroLogo}
                ldJson={ldJson}
            />

            {/* HERO */}
            <section className="relative isolate overflow-hidden bg-[#001B3D] text-white">
                {/* Decorative background: giant logo watermark */}
                <div aria-hidden="true" className="pointer-events-none absolute -right-24 -bottom-24 w-[560px] h-[560px] opacity-[0.08] hidden lg:block">
                    <img src={IMAGES.heroLogo} alt="" className="h-full w-full object-contain" />
                </div>
                {/* Subtle radial highlight */}
                <div className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(60% 55% at 15% 25%, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0) 60%)",
                    }}
                />
                {/* Bottom accent border */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                <div className="relative container-ys py-16 sm:py-20 lg:py-24">
                    <div className="grid lg:grid-cols-2 gap-10 items-start">
                        <div>
                            {/* Logo hero mark — original brand asset */}
                            <div className="ys-float inline-flex items-center gap-4 mb-6">
                                <img
                                    src={IMAGES.heroLogo}
                                    alt={BRAND.logoAlt}
                                    className="h-24 w-24 sm:h-28 sm:w-28 rounded-2xl object-contain shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
                                    width={112}
                                    height={112}
                                />
                                <div className="leading-tight">
                                    <div className="font-heading text-2xl font-bold">YS Removals</div>
                                    <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-white/70">Your Man With Van</div>
                                </div>
                            </div>
                            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-medium uppercase tracking-wide">
                                London removals & man and van
                            </span>
                            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight font-heading">
                                Reliable House and Office Removals Across London
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-white/85 max-w-xl">
                                Careful handling, clear communication and flexible moving support for homes, flats, offices and individual items.
                            </p>
                            <div className="mt-6 flex flex-col sm:flex-row gap-3">
                                <Link to="/request-a-quote">
                                    <Button data-testid={HOME.heroPrimaryCta} className="bg-white hover:bg-white/90 text-[#001B3D] font-semibold h-11 px-6 w-full sm:w-auto">
                                        Get a Free Quote
                                    </Button>
                                </Link>
                                <a href={waLink()} target="_blank" rel="noreferrer" className="w-full sm:w-auto">
                                    <Button data-testid={HOME.heroWhatsappCta} className="h-11 px-6 bg-[#25D366] hover:bg-[#20b955] text-white font-semibold w-full sm:w-auto">
                                        <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp Us
                                    </Button>
                                </a>
                                <a href={CONTACT.phoneHref} className="w-full sm:w-auto">
                                    <Button data-testid={HOME.heroCallCta} variant="ghost" className="h-11 px-6 text-white hover:bg-white/10 w-full sm:w-auto">
                                        <Phone className="h-4 w-4 mr-2" /> Call Now
                                    </Button>
                                </a>
                            </div>
                            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
                                {TRUST_POINTS.map((t) => (
                                    <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-white" /> {t}</span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <HeroQuoteForm />
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST STRIP */}
            <div className="bg-[#001B3D] text-white border-y border-white/10">
                <div className="container-ys py-4 flex gap-3 overflow-x-auto sm:flex-wrap sm:justify-center">
                    {TRUST_POINTS.map((t) => (
                        <span key={t} className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 border border-white/15 text-sm text-white whitespace-nowrap">
                            <CheckCircle2 className="h-4 w-4 text-white" /> {t}
                        </span>
                    ))}
                </div>
            </div>

            {/* SERVICES */}
            <Section>
                <div data-testid={HOME.servicesSection}>
                    <SectionHeader
                        eyebrow="What we do"
                        title="London removals, done properly"
                        subtitle="From full house moves to a single item delivery — pick the service that fits your job."
                    />
                    <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {SERVICES.map((s) => (
                            <ServiceCard key={s.slug} service={s} />
                        ))}
                    </div>
                </div>
            </Section>

            {/* WHY CHOOSE US */}
            <Section tone="soft">
                <SectionHeader
                    eyebrow="Why YS Removals"
                    title="Practical benefits, honestly stated"
                    subtitle="We keep it straightforward: careful handling, clear pricing and calm, professional service."
                />
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {benefits.map((b) => (
                        <div key={b.title} className="rounded-xl bg-white p-5 border border-[#17252D]/10">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#001B3D]/10 text-[#001B3D]">
                                <b.icon className="h-5 w-5" strokeWidth={1.75} />
                            </span>
                            <div className="mt-3 font-semibold">{b.title}</div>
                            <div className="mt-1 text-sm text-[#17252D]/75">{b.body}</div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* HOW IT WORKS */}
            <Section>
                <SectionHeader
                    eyebrow="How it works"
                    title="A simple, four-step process"
                />
                <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {steps.map((s, i) => (
                        <li key={s.title} className="rounded-xl border border-[#17252D]/10 p-5 bg-white">
                            <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#001B3D] text-white font-semibold text-sm">{i + 1}</div>
                            <div className="mt-3 font-semibold">{s.title}</div>
                            <div className="mt-1 text-sm text-[#17252D]/75">{s.body}</div>
                        </li>
                    ))}
                </ol>
            </Section>

            {/* QUOTE TEASER */}
            <section className="bg-[#F3F7F9]">
                <div className="container-ys section grid lg:grid-cols-2 gap-8 items-center">
                    <div>
                        <span className="eyebrow">Tailored quotations</span>
                        <h2 className="mt-3 text-3xl font-semibold tracking-tight">A clear quote based on your actual move</h2>
                        <p className="mt-3 text-[#17252D]/75">
                            We don’t use pop-up prices that don’t reflect your job. Share your details and we’ll come back with a tailored quotation you can rely on.
                        </p>
                        <ul className="mt-4 space-y-2 text-sm">
                            {["Inventory & rooms", "Property type & floor", "Access & parking", "Dates & flexibility"].map((x) => (
                                <li key={x} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#001B3D]" /> {x}</li>
                            ))}
                        </ul>
                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <Link to="/request-a-quote"><Button className="bg-[#001B3D] hover:bg-[#001B3D]/90 h-11 px-6 text-white">Start your quote</Button></Link>
                            <a href={waLink()} target="_blank" rel="noreferrer">
                                <Button variant="outline" className="h-11 px-6 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp quick quote</Button>
                            </a>
                        </div>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-[#17252D]/10 bg-white">
                        <img src={IMAGES.services} alt="Removals workers carefully packing boxes" className="w-full h-full object-cover aspect-[4/3]" loading="lazy" />
                    </div>
                </div>
            </section>

            {/* AREAS — compact */}
            <section className="bg-[#F3F7F9]">
                <div className="container-ys py-10 sm:py-12">
                    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <span className="eyebrow">Areas we cover</span>
                        <h2 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight">London-wide moving service</h2>
                    </div>
                    <Link
                        to="/areas"
                        className="inline-flex items-center gap-1 text-sm font-semibold text-[#001B3D] hover:gap-2 transition-[gap] duration-200"
                    >
                        View all areas <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                <div className="grid gap-3 grid-cols-2 sm:grid-cols-5">
                    {[
                        { label: "Central London", note: "WC, EC, W1" },
                        { label: "North London",   note: "N, NW"      },
                        { label: "South London",   note: "SE, SW"     },
                        { label: "East London",    note: "E, IG"      },
                        { label: "West London",    note: "W, TW"      },
                    ].map((a) => (
                        <Link
                            key={a.label}
                            to="/areas"
                            className="group rounded-xl bg-white border border-[#001B3D]/10 p-4 hover:border-[#001B3D]/40 hover:shadow-[0_6px_20px_rgba(0,27,61,0.08)] transition-all duration-200 flex flex-col items-start"
                        >
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#001B3D]/10 text-[#001B3D] mb-2">
                                <MapPin className="h-4 w-4" />
                            </span>
                            <span className="text-sm font-semibold text-[#001B3D]">{a.label}</span>
                            <span className="text-[11px] text-[#17252D]/60">{a.note}</span>
                        </Link>
                    ))}
                </div>
                </div>
            </section>

            {/* GOOGLE REVIEWS — Business Profile connected */}
            <section className="bg-[#001B3D] text-white">
                <div className="container-ys section">
                    <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center">
                        <div>
                            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-medium uppercase tracking-wide">
                                <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
                                Google Reviews
                            </span>
                            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight font-heading">Verified reviews from real customers</h2>
                            <p className="mt-3 text-white/80 max-w-xl">
                                We publish reviews directly from our official YS Removals Google Business Profile — nothing rewritten, shortened or fabricated. Read every review on Google below, or ask us to enable live in-page reviews (requires a Google Places API key).
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <a
                                    href={BRAND.googleBusinessUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <Button className="bg-white hover:bg-white/90 text-[#001B3D] font-semibold h-11 px-6">
                                        <Star className="h-4 w-4 mr-2 text-[#FBBC05]" fill="#FBBC05" /> View All Reviews on Google
                                    </Button>
                                </a>
                                <a href={waLink()} target="_blank" rel="noreferrer">
                                    <Button className="h-11 px-6 bg-[#25D366] hover:bg-[#20b955] text-white font-semibold">
                                        <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp Us
                                    </Button>
                                </a>
                            </div>
                        </div>
                        <div data-testid={HOME.reviewsPlaceholder} className="rounded-2xl border border-white/15 bg-white/5 p-6">
                            <div className="flex items-center gap-2 text-white/85 text-sm font-medium">
                                <Info className="h-4 w-4" />
                                <span>Business Profile linked — reviews load from Google</span>
                            </div>
                            <p className="mt-3 text-sm text-white/75 leading-relaxed">
                                We only publish real reviews from the official YS Removals Google Business Profile.
                                Read every review directly on Google using the button on the left, or add a Google Places API key later to display live reviews inline on this page.
                            </p>
                            <ul className="mt-4 space-y-2 text-sm text-white/80">
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-white/70" /> Every review is genuine and unedited</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-white/70" /> Reviewer names, ratings and wording preserved</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-white/70" /> No fake ratings or invented testimonials</li>
                                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-white/70" /> Live overall rating available when API is enabled</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <Section tone="soft">
                <SectionHeader eyebrow="FAQ" title="Common questions" />
                <div className="rounded-2xl bg-white border border-[#17252D]/10 p-2 sm:p-4">
                    <Accordion type="single" collapsible data-testid={HOME.faqAccordion}>
                        {homeFaqs.map((f, i) => (
                            <AccordionItem key={i} value={`f-${i}`}>
                                <AccordionTrigger data-testid={HOME.faqItemTrigger} className="text-left font-heading">{f.q}</AccordionTrigger>
                                <AccordionContent className="text-[#17252D]/75">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </Section>

            {/* MOVING TIPS PREVIEW */}
            <Section>
                <SectionHeader
                    eyebrow="Moving tips"
                    title="Practical advice for your move"
                />
                <div className="grid gap-6 md:grid-cols-3">
                    {[
                        { slug: "moving-house-checklist", title: "Moving House Checklist", body: "A week-by-week timeline to stay organised in the run-up to moving day." },
                        { slug: "how-to-pack-fragile-items", title: "How to Pack Fragile Items", body: "Techniques for glassware, ceramics and electronics." },
                        { slug: "parking-and-access-for-a-london-move", title: "Parking & Access in London", body: "Reserving space, permits and what to check before the day." },
                    ].map((p, i) => (
                        <Link key={p.slug} to={`/moving-tips/${p.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-[#17252D]/10 hover:border-[#001B3D]/40 transition-[border-color] duration-200">
                            <div className="aspect-[16/9] overflow-hidden">
                                <img src={i === 0 ? IMAGES.blog1 : IMAGES.blog2} alt="" loading="lazy" className="h-full w-full object-cover" />
                            </div>
                            <div className="p-4">
                                <div className="font-heading font-semibold">{p.title}</div>
                                <p className="mt-1 text-sm text-[#17252D]/75">{p.body}</p>
                                <span className="mt-3 inline-flex items-center gap-1 text-sm text-[#001B3D]">Read guide <ArrowRight className="h-4 w-4" /></span>
                            </div>
                        </Link>
                    ))}
                </div>
            </Section>

            <CtaBand subtitle="Call, WhatsApp or send us the details and we’ll come back with a tailored quotation." />
        </div>
    );
}
